import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import dailyIntakeService from "../../services/dailyIntakeService";
import patientService from "../../services/patientService";
import { useResolvedPatientId } from "../../hooks/useResolvedPatientId";
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";

const DailyIntakeHistory = () => {
  const { patientId, loading: resolvingPatientId, error: patientIdError } = useResolvedPatientId();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [patientName, setPatientName] = useState("");
  const [stats, setStats] = useState({
    totalEntries: 0,
    currentStreak: 0,
    longestStreak: 0,
    completionRate: 0,
  });

  // Now include loadHistory in dependencies
  // ✅ ADDED - Helper functions to calculate stats from array
  const calculateCurrentStreak = (logs) => {
    if (!logs || logs.length === 0) return 0;

    // Sort by date descending (newest first)
    const sortedLogs = [...logs].sort(
      (a, b) => new Date(b.date) - new Date(a.date)
    );

    let streak = 0;
    let currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    for (const log of sortedLogs) {
      const logDate = new Date(log.date);
      logDate.setHours(0, 0, 0, 0);

      const daysDiff = Math.floor(
        (currentDate - logDate) / (1000 * 60 * 60 * 24)
      );

      if (daysDiff === streak) {
        streak++;
        currentDate.setDate(currentDate.getDate() - 1);
      } else {
        break;
      }
    }

    return streak;
  };

  const calculateLongestStreak = (logs) => {
    if (!logs || logs.length === 0) return 0;

    const sortedLogs = [...logs].sort(
      (a, b) => new Date(a.date) - new Date(b.date)
    );

    let maxStreak = 1;
    let currentStreak = 1;

    for (let i = 1; i < sortedLogs.length; i++) {
      const prevDate = new Date(sortedLogs[i - 1].date);
      const currDate = new Date(sortedLogs[i].date);

      prevDate.setHours(0, 0, 0, 0);
      currDate.setHours(0, 0, 0, 0);

      const daysDiff = Math.floor(
        (currDate - prevDate) / (1000 * 60 * 60 * 24)
      );

      if (daysDiff === 1) {
        currentStreak++;
        maxStreak = Math.max(maxStreak, currentStreak);
      } else {
        currentStreak = 1;
      }
    }

    return maxStreak;
  };

  const calculateCompletionRate = (logs) => {
    if (!logs || logs.length === 0) return 0;

    // Calculate days since first entry
    const sortedLogs = [...logs].sort(
      (a, b) => new Date(a.date) - new Date(b.date)
    );
    const firstDate = new Date(sortedLogs[0].date);
    const today = new Date();

    const daysSinceStart =
      Math.floor((today - firstDate) / (1000 * 60 * 60 * 24)) + 1;

    return Math.round((logs.length / daysSinceStart) * 100);
  };

  const loadHistory = useCallback(async () => {
    if (!patientId) return;
    try {
      setLoading(true);
      setError(null);
      let actualPatientId = patientId;

      // ✅ Resolve "me" to actual patient ID
      if (patientId === "me") {
        console.log('🔍 Resolving "me" to actual patient ID...');

        const meResponse = await patientService.getCurrentUserPatient();

        if (meResponse.success) {
          actualPatientId = meResponse.data.id;
          console.log(`✅ Resolved "me" to patient ID: ${actualPatientId}`);

          setPatientName(
            `${meResponse.data.firstName} ${meResponse.data.lastName}`
          );
        } else {
          console.error("❌ Failed to resolve patient ID");
          setError(
            "Failed to load patient profile. Please create a profile first."
          );
          return;
        }
      } else {
        // Load patient info normally for numeric IDs
        const patientResponse = await patientService.getPatientById(patientId);
        if (patientResponse.success) {
          const patientData = patientResponse.data;
          setPatientName(`${patientData.firstName} ${patientData.lastName}`);
        } else {
          setError("Failed to load patient information");
          return;
        }
      }

      // ✅ Load intake history using the resolved ID
      console.log(
        `📡 Loading intake history for patient ${actualPatientId}...`
      );
      const historyResponse = await dailyIntakeService.getHistory(
        actualPatientId
      );

      if (historyResponse.success) {
        // ✅ FIXED - Backend returns an array, not an object
        const historyData = historyResponse.data;

        // Check if it's an array or object
        if (Array.isArray(historyData)) {
          console.log(
            `✅ Loaded ${historyData.length} intake records (array format)`
          );

          // Backend returned array directly
          setLogs(historyData);

          // Calculate stats from the array
          const totalEntries = historyData.totalEntries || 0;
          const currentStreak = historyData.currentStreak || 0;
          const longestStreak = historyData.longestStreak || 0;
          const completionRate = historyData.completionRate || 0;

          setStats({
            totalEntries,
            currentStreak,
            longestStreak,
            completionRate,
          });
        } else {
          console.log(
            `✅ Loaded ${
              historyData.logs?.length || 0
            } intake records (object format)`
          );

          // Backend returned object with logs property
          setLogs(historyData.logs || []);
          setStats({
            totalEntries: historyData.totalEntries || 0,
            currentStreak: historyData.currentStreak || 0,
            longestStreak: historyData.longestStreak || 0,
            completionRate: historyData.completionRate || 0,
          });
        }
      } else {
        setError(historyResponse.message || "Failed to load intake history");
      }
    } catch (err) {
      console.error("❌ Error loading history:", err);
      setError("Failed to load intake history");
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const getSymptomSeverity = (value) => {
    if (value <= 3) return { label: "Mild", color: "success" };
    if (value <= 6) return { label: "Moderate", color: "warning" };
    return { label: "Severe", color: "error" };
  };

  if (resolvingPatientId) {
    return (
      <Container sx={{ mt: 4, textAlign: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (patientIdError) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{patientIdError}</Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container sx={{ mt: 4, textAlign: "center" }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading history...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => navigate("/patients/new")}>
          Create Patient Profile
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      {/* Header */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 3,
          background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
          color: "white",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Daily Health Log History
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, mt: 1 }}>
              {patientName}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate(`/patients/${patientId}/daily-intake/report`)}
              sx={{
                color: "white",
                borderColor: "rgba(255,255,255,0.6)",
                "&:hover": { borderColor: "white", bgcolor: "rgba(255,255,255,0.1)" },
              }}
            >
              Doctor Visit Report
            </Button>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => navigate(`/patients/${patientId}/daily-intake`)}
              sx={{
                bgcolor: "white",
                color: "primary.main",
                "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
              }}
            >
              New Entry Today
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <CalendarMonthIcon
                  sx={{ fontSize: 40, color: "primary.main" }}
                />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.totalEntries}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Entries
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <LocalFireDepartmentIcon
                  sx={{ fontSize: 40, color: "error.main" }}
                />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.currentStreak}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Current Streak
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <TrendingUpIcon sx={{ fontSize: 40, color: "success.main" }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.longestStreak}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Longest Streak
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Completion Rate
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={stats.completionRate}
                  sx={{ flex: 1, height: 8, borderRadius: 4 }}
                />
                <Typography variant="h6" fontWeight="bold">
                  {stats.completionRate}%
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* History Table */}
      <Paper elevation={2}>
        <Box sx={{ p: 2, bgcolor: "primary.main", color: "white" }}>
          <Typography variant="h6" fontWeight="bold">
            Entry History
          </Typography>
        </Box>

        {logs.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="h6" color="text.secondary">
              No entries yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Click "New Entry Today" to get started
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Date</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Overall Well-being</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Symptoms</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Meds Taken</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Activity</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Alerts</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Actions</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log, index) => {
                  const avgSymptoms = Math.round(
                    (log.fatigue +
                      log.shortnessOfBreath +
                      log.chestPain +
                      log.swelling +
                      log.nausea +
                      log.dizziness +
                      log.jointPain +
                      log.numbnessTingling) /
                      8
                  );
                  const symptomInfo = getSymptomSeverity(avgSymptoms);

                  return (
                    <TableRow key={index} hover>
                      <TableCell>
                        {new Date(log.date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${log.overallWellbeing}/5`}
                          color={
                            log.overallWellbeing >= 4
                              ? "success"
                              : log.overallWellbeing >= 3
                              ? "warning"
                              : "error"
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${symptomInfo.label} (${avgSymptoms}/10)`}
                          color={symptomInfo.color}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.medicationsTaken ? "Yes" : "Missed"}
                          color={log.medicationsTaken ? "success" : "error"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.activityLevel}
                          variant="outlined"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {(log.newSymptoms ||
                          log.emergencyRoom ||
                          log.fellToday) && (
                          <Chip label="⚠️ Alert" color="warning" size="small" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() =>
                              navigate(
                                `/patients/${patientId}/daily-intake/${log.id}`
                              )
                            }
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Container>
  );
};

export default DailyIntakeHistory;